import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_CONFIG from '../../apiConfig';
import ImageUpload from '../image-editing/ImageUpload';
import InsufficientCreditsModal from '../ui/InsufficientCreditsModal';
import { useModeRestrictions } from '../../hooks/useModeRestrictions';
import { useCreditEstimation } from '../../hooks/useCreditEstimation';

// Modular Components
import { WorkflowStepper } from '../ai-workflow/components/WorkflowStepper';
import { GeneratedResults } from '../ai-workflow/components/GeneratedResults';
import { SummarySidebar } from '../ai-workflow/components/SummarySidebar';
import { CatalogueWorkflowConfig, ECOMMERCE_CATALOGUE_CONFIG, ActiveWearExamplePreset, EcommerceExamplePreset } from './catalogueWorkflowConfigs';
import {
  CatalogueGeneratePresetItem,
  CataloguePresetPose,
  buildWearCataloguePayload,
  flattenWearInputImages,
  groupSelectedPosesByGarment,
  normalizeCataloguePreset,
} from './wearCatalogueUtils';

type Step = 'SELECT_PRODUCTS' | 'SELECT_MODELS' | 'SELECT_POSES' | 'SUMMARY';

const getPoseCategory = (poseName: string): string => {
  const name = poseName.toLowerCase();
  if (name === 'full') return 'full';
  if (name === 'upper' || name === 'closeup-upper') return 'upper';
  if (name === 'lower' || name === 'closeup-bottom') return 'lower';
  if (name === 'back') return 'back';
  if (name === 'left' || name === 'left-closeup' || name === 'closeup-left') return 'left';
  if (name === 'right' || name === 'right-closeup' || name === 'closeup-right') return 'right';
  return 'left'; // default/fallback
};

const getFieldKeys = (category: string) => {
  let suffix = '';
  if (category === 'full') suffix = 'Full';
  else if (category === 'upper') suffix = 'Upper';
  else if (category === 'lower') suffix = 'Lower';
  else if (category === 'back') suffix = 'Back';
  else if (category === 'left') suffix = 'Left';
  else if (category === 'right') suffix = 'Right';

  return {
    refKey: `reference${suffix}PersonImageUrl`,
    clothesKey: `clothes${suffix}ImageUrl`
  };
};

interface UpperBodyFashionCatalogueWorkflowProps {
  onBackToWorkflows: () => void;
  config?: CatalogueWorkflowConfig;
  initialInputImages?: string[];
  initialGeneratedImages?: string[];
  initialModelImageUrl?: string;
  initialBackgroundImageUrl?: string;
}

const UpperBodyFashionCatalogueWorkflow: React.FC<UpperBodyFashionCatalogueWorkflowProps> = ({
  onBackToWorkflows,
  config = ECOMMERCE_CATALOGUE_CONFIG,
  initialInputImages,
  initialGeneratedImages,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as any;
  const { currentUser, refreshUserCredits, userCredits } = useAuth();

  const inputImagesToUse = flattenWearInputImages(initialInputImages || state?.inputImages);
  const generatedImagesToUse = initialGeneratedImages || state?.generatedImages;

  // Step state
  const [currentStep, setCurrentStep] = useState<Step>('SELECT_PRODUCTS');

  // Input images state
  const [fullTopUrl, setFullTopUrl] = useState('');
  const [closeUpTopUrl, setCloseUpTopUrl] = useState('');
  const [closeUpSleevelessUrl, setCloseUpSleevelessUrl] = useState('');
  const [bottomUrl, setBottomUrl] = useState('');
  const [backUrl, setBackUrl] = useState('');

  // Presets from backend
  const [presets, setPresets] = useState<CatalogueGeneratePresetItem[]>([]);
  const [presetsLoading, setPresetsLoading] = useState(true);

  // Selections
  const [selectedModel, setSelectedModel] = useState<CatalogueGeneratePresetItem | null>(null);
  const [selectedPosesMap, setSelectedPosesMap] = useState<Record<string, CataloguePresetPose>>({});
  const selectedPoseNames = React.useMemo(() => Object.keys(selectedPosesMap), [selectedPosesMap]);

  // Generation result state
  const [generatedImages, setGeneratedImages] = useState<string[]>([]);
  const [suffixPrompts, setSuffixPrompts] = useState<Record<string, string>>({});
  const [mode, setMode] = useState('balanced');
  const [enable4k, setEnable4k] = useState(true);

  const [isExample, setIsExample] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStage, setProcessingStage] = useState('');
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);

  const isActiveWearLayout = config.inputLayout === 'active-wear';
  const isWearCatalogue = Boolean(config.category);
  const garmentUploadUrl = config.uploadUrl || API_CONFIG.AI_WORKFLOW_UPLOAD_URL;

  const getGarmentUrlForCategory = useCallback((category: string): string => {
    if (category === 'full') return fullTopUrl;
    if (category === 'upper') return closeUpTopUrl;
    if (category === 'lower') return bottomUrl;
    if (category === 'back') return backUrl;
    return closeUpSleevelessUrl;
  }, [fullTopUrl, closeUpTopUrl, bottomUrl, backUrl, closeUpSleevelessUrl]);

  const getUploadRequirementLabel = useCallback((category: string): string => {
    if (isActiveWearLayout) {
      if (category === 'upper') return 'Top';
      if (category === 'lower') return 'Bottom';
      if (category === 'back') return 'Back';
      if (category === 'full') return 'Full Body';
      return 'Garment';
    }

    if (category === 'upper') return 'Close-up Top';
    if (category === 'lower') return 'Full Bottom';
    if (category === 'back') return 'Back';
    if (category === 'full') return 'Full Top';
    return 'Close-up Sleeve';
  }, [isActiveWearLayout]);

  const modeRestrictions = useModeRestrictions();

  // Local credit estimation
  const [creditEstimation, estimateCreditCost] = useCreditEstimation(config.creditType);

  // Load initial generated images if passed
  useEffect(() => {
    if (generatedImagesToUse && generatedImagesToUse.length > 0) {
      setGeneratedImages(generatedImagesToUse);
      setCurrentStep('SUMMARY');
    }
  }, [generatedImagesToUse]);

  // Clear generated images when navigating away from the summary step
  useEffect(() => {
    if (currentStep !== 'SUMMARY') {
      setGeneratedImages([]);
    }
  }, [currentStep]);

  const [generationTime, setGenerationTime] = useState<number>(0);
  const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(null);

  const startTimer = () => {
    setGenerationTime(0);
    const interval = setInterval(() => {
      setGenerationTime(prev => prev + 0.1);
    }, 100);
    setTimerInterval(interval);
  };

  const stopTimer = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
  };

  useEffect(() => {
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [timerInterval]);

  // Fetch presets on component mount
  useEffect(() => {
    if (presets.length > 0) return;

    const fetchPresets = async () => {
      try {
        setPresetsLoading(true);
        const res = await fetch(config.presetsUrl);
        if (!res.ok) throw new Error('Failed to fetch catalogue presets');
        const data = await res.json();
        if (data.success && Array.isArray(data.presets) && data.presets.length > 0) {
          const fetchedPresets: CatalogueGeneratePresetItem[] = data.presets.map(
            (preset: Record<string, unknown>) => normalizeCataloguePreset(preset),
          );
          setPresets(fetchedPresets);

          if (inputImagesToUse && inputImagesToUse.length > 0) {
            let activeModel = fetchedPresets[0];
            const initialPosesMap: Record<string, CataloguePresetPose> = {};

            if (isWearCatalogue && state?.inputImages && !Array.isArray(state.inputImages)) {
              Object.entries(state.inputImages as Record<string, string[]>).forEach(([poseName, pair]) => {
                const faceUrl = pair?.[0];
                const garmentUrl = pair?.[1];
                if (!faceUrl || !garmentUrl) return;

                const foundPreset = fetchedPresets.find((preset: CatalogueGeneratePresetItem) => (
                  preset.faceImageUrl === faceUrl || preset.profilePic === faceUrl
                ));
                const foundPose = foundPreset?.poses.find((pose: CataloguePresetPose) => pose.poseName === poseName);
                if (!foundPreset || !foundPose) return;

                activeModel = foundPreset;
                const cat = getPoseCategory(foundPose.poseName);
                initialPosesMap[cat] = foundPose;

                if (cat === 'full') {
                  setFullTopUrl(garmentUrl);
                } else if (cat === 'upper') {
                  setCloseUpTopUrl(garmentUrl);
                } else if (cat === 'lower') {
                  setBottomUrl(garmentUrl);
                } else if (cat === 'back') {
                  setBackUrl(garmentUrl);
                } else {
                  setCloseUpSleevelessUrl(garmentUrl);
                }
              });
            } else {
              for (let i = 0; i < inputImagesToUse.length; i += 2) {
                const refUrl = inputImagesToUse[i];
                const garmentUrl = inputImagesToUse[i + 1];
                if (!refUrl || !garmentUrl) continue;

                let foundPose: CataloguePresetPose | undefined;
                let foundPreset: CatalogueGeneratePresetItem | undefined;
                for (const preset of fetchedPresets) {
                  const pose = preset.poses.find((item: CataloguePresetPose) => (
                    item.cloudStorageUrl === refUrl
                    || (isWearCatalogue && preset.faceImageUrl === refUrl)
                  ));
                  if (pose) {
                    foundPose = pose;
                    foundPreset = preset;
                    break;
                  }
                }

                if (foundPose && foundPreset) {
                  activeModel = foundPreset;
                  const cat = getPoseCategory(foundPose.poseName);
                  initialPosesMap[cat] = foundPose;

                  if (cat === 'full') {
                    setFullTopUrl(garmentUrl);
                  } else if (cat === 'upper') {
                    setCloseUpTopUrl(garmentUrl);
                  } else if (cat === 'lower') {
                    setBottomUrl(garmentUrl);
                  } else if (cat === 'back') {
                    setBackUrl(garmentUrl);
                  } else {
                    setCloseUpSleevelessUrl(garmentUrl);
                  }
                }
              }
            }
            setSelectedModel(activeModel);
            setSelectedPosesMap(initialPosesMap);
          } else {
            setSelectedModel(fetchedPresets[0]);
            setSelectedPosesMap({});
          }
        } else {
          setPresets([]);
        }
      } catch (e) {
        console.error('Error fetching catalogue presets:', e);
        setPresets([]);
      } finally {
        setPresetsLoading(false);
      }
    };
    fetchPresets();
  }, [inputImagesToUse, presets.length, config.presetsUrl, isWearCatalogue, state?.inputImages]);

  // Estimate credit cost using API
  useEffect(() => {
    if (!selectedModel || selectedPoseNames.length === 0) {
      if (!isWearCatalogue) {
        estimateCreditCost({
          referenceFullPersonImageUrl: "",
          mode: mode.toLowerCase(),
          enable4k: enable4k,
        });
      }
      return;
    }

    if (isWearCatalogue && config.category) {
      const selectedPoses = selectedPoseNames
        .map((category) => selectedPosesMap[category])
        .filter((pose): pose is CataloguePresetPose => Boolean(pose));
      const garmentGroups = groupSelectedPosesByGarment(
        selectedPoseNames,
        selectedPosesMap,
        getGarmentUrlForCategory,
      );

      if (garmentGroups.size === 0 || selectedPoses.length === 0) {
        return;
      }

      const clothesImageUrl = Array.from(garmentGroups.keys())[0];

      estimateCreditCost(buildWearCataloguePayload(
        selectedModel,
        selectedPoses,
        clothesImageUrl,
        config.category,
        mode,
        enable4k,
        currentUser?.uid || 'anonymous',
        suffixPrompts,
      ));
      return;
    }

    const payload: Record<string, any> = {
      mode: mode.toLowerCase(),
      referenceFullPersonImageUrl: "",
      enable4k: enable4k,
    };

    selectedPoseNames.forEach(category => {
      const poseObj = selectedPosesMap[category];
      if (poseObj) {
        const { refKey, clothesKey } = getFieldKeys(category);
        payload[refKey] = poseObj.cloudStorageUrl;
        if (category === 'full') {
          payload[clothesKey] = fullTopUrl || "";
        } else if (category === 'upper') {
          payload[clothesKey] = closeUpTopUrl || "";
        } else if (category === 'lower') {
          payload[clothesKey] = bottomUrl || "";
        } else if (category === 'back') {
          payload[clothesKey] = backUrl || "";
        } else {
          payload[clothesKey] = closeUpSleevelessUrl || "";
        }
      }
    });

    estimateCreditCost(payload);
  }, [mode, enable4k, selectedPoseNames, selectedPosesMap, selectedModel, fullTopUrl, closeUpTopUrl, closeUpSleevelessUrl, bottomUrl, backUrl, estimateCreditCost, isWearCatalogue, config.category, suffixPrompts, currentUser?.uid, getGarmentUrlForCategory]);

  // Compile input images
  const inputImageUrls = isActiveWearLayout
    ? [closeUpTopUrl, bottomUrl, backUrl, fullTopUrl].filter(Boolean)
    : [fullTopUrl, closeUpTopUrl, closeUpSleevelessUrl, bottomUrl, backUrl].filter(Boolean);

  // API Call to generate Upper Body Catalogue Lookbook
  const handleGenerate = async () => {
    if (!selectedModel) {
      alert('Please select a model preset first.');
      return;
    }
    if (selectedPoseNames.length === 0) {
      alert('Please select at least one pose/angle to generate.');
      return;
    }
    if (!creditEstimation.canAfford) {
      setShowInsufficientCreditsModal(true);
      return;
    }

    setIsProcessing(true);
    setProcessingStage('Initializing lookbook generation...');
    startTimer();

    try {
      setProcessingStage('Pairing selected angle references...');

      if (isWearCatalogue && config.category) {
        const garmentGroups = groupSelectedPosesByGarment(
          selectedPoseNames,
          selectedPosesMap,
          getGarmentUrlForCategory,
        );

        if (garmentGroups.size === 0) {
          throw new Error('Please upload garment images for the selected poses.');
        }

        const allOutputUrls: string[] = [];

        for (const [garmentUrl, poses] of Array.from(garmentGroups.entries())) {
          setProcessingStage(`Generating ${poses.length} pose${poses.length > 1 ? 's' : ''}...`);

          const payload = buildWearCataloguePayload(
            selectedModel,
            poses,
            garmentUrl,
            config.category,
            mode,
            enable4k,
            currentUser?.uid || 'anonymous',
            suffixPrompts,
          );

          const res = await fetch(config.generateUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });

          if (!res.ok) {
            const errorText = await res.text();
            throw new Error(errorText || 'Server error during generation');
          }

          const data = await res.json();
          if (data.success && data.outputImageUrls) {
            allOutputUrls.push(...Object.values(data.outputImageUrls) as string[]);
          } else if (data.statusCode === 501) {
            setShowInsufficientCreditsModal(true);
            return;
          } else {
            throw new Error(data.message || 'Generation returned unsuccessful status');
          }
        }

        setGeneratedImages(allOutputUrls);
        await refreshUserCredits();
        return;
      }

      // Construct suffixPrompt dictionary matching selected pose names mapped to backend keys
      const cleanedSuffixPrompts: Record<string, string> = {};
      selectedPoseNames.forEach(category => {
        const poseObj = selectedPosesMap[category];
        if (poseObj) {
          const poseName = poseObj.poseName;
          const backendKey = poseName === 'left-closeup' ? 'left' : (poseName === 'right-closeup' ? 'right' : poseName);
          if (suffixPrompts[poseName]?.trim()) {
            cleanedSuffixPrompts[backendKey] = suffixPrompts[poseName].trim();
          }
        }
      });

      // Construct request body dynamically based on selected poses
      const payload: Record<string, any> = {
        userId: currentUser?.uid || 'anonymous',
        numImages: 1, // backend computes count per pairs
        mode: mode.toLowerCase(),
        enable4k: enable4k,
        suffixPrompt: Object.keys(cleanedSuffixPrompts).length > 0 ? cleanedSuffixPrompts : undefined
      };

      selectedPoseNames.forEach(category => {
        const poseObj = selectedPosesMap[category];
        if (poseObj) {
          const { refKey, clothesKey } = getFieldKeys(category);

          // Assign model reference image URL
          payload[refKey] = poseObj.cloudStorageUrl;

          // Assign corresponding uploaded garment view URL
          if (category === 'full') {
            payload[clothesKey] = fullTopUrl;
          } else if (category === 'upper') {
            payload[clothesKey] = closeUpTopUrl;
          } else if (category === 'lower') {
            payload[clothesKey] = bottomUrl;
          } else if (category === 'back') {
            payload[clothesKey] = backUrl;
          } else {
            // fallback/default left closeup mapping
            payload[clothesKey] = closeUpSleevelessUrl;
          }
        }
      });

      setProcessingStage('Submitting garment perspectives to GPU cluster...');
      const res = await fetch(config.generateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || 'Server error during generation');
      }

      setProcessingStage('Synthesizing lookbook angles...');
      const data = await res.json();
      if (data.success && data.outputImageUrls) {
        setProcessingStage('Finalizing output formats...');
        const imageUrls = Object.values(data.outputImageUrls) as string[];
        setGeneratedImages(imageUrls);
        await refreshUserCredits();
      } else {
        throw new Error(data.message || 'Generation returned unsuccessful status');
      }
    } catch (e) {
      console.error(e);
      alert(e instanceof Error ? e.message : 'Error generating fashion catalogue');
    } finally {
      stopTimer();
      setIsProcessing(false);
      setProcessingStage('');
    }
  };

  const steps: { key: Step; label: string }[] = [
    { key: 'SELECT_PRODUCTS', label: 'Upload Perspectives' },
    { key: 'SELECT_MODELS', label: 'Select Model' },
    { key: 'SELECT_POSES', label: 'Select Poses' },
    { key: 'SUMMARY', label: 'Summary & Generate' },
  ];

  const nextStep = () => {
    const idx = steps.findIndex(s => s.key === currentStep);
    if (currentStep === 'SELECT_PRODUCTS') {
      if (isActiveWearLayout) {
        if (!closeUpTopUrl) {
          alert('Please upload the required garment perspective: Top.');
          return;
        }
      } else if (!fullTopUrl) {
        alert('Please upload the required garment perspective: Full Top View.');
        return;
      }
    }
    if (currentStep === 'SELECT_MODELS') {
      if (!selectedModel) {
        alert('Please select a model preset.');
        return;
      }
    }
    if (currentStep === 'SELECT_POSES') {
      if (selectedPoseNames.length === 0) {
        alert('Please select at least one pose/angle.');
        return;
      }
    }
    if (idx < steps.length - 1) setCurrentStep(steps[idx + 1].key);
  };

  const prevStep = () => {
    const idx = steps.findIndex(s => s.key === currentStep);
    if (idx <= 0) {
      onBackToWorkflows();
      return;
    }
    setCurrentStep(steps[idx - 1].key);
  };

  const goToStep = (step: Step) => {
    const stepIdx = steps.findIndex(s => s.key === step);
    const currentIdx = steps.findIndex(s => s.key === currentStep);
    if (stepIdx >= 0 && stepIdx < currentIdx) {
      setCurrentStep(step);
    }
  };

  const handleStartNew = () => {
    setGeneratedImages([]);
    setFullTopUrl('');
    setCloseUpTopUrl('');
    setCloseUpSleevelessUrl('');
    setBottomUrl('');
    setBackUrl('');
    if (presets.length > 0) {
      setSelectedModel(presets[0]);
      setSelectedPosesMap({});
    } else {
      setSelectedModel(null);
      setSelectedPosesMap({});
    }
    setSuffixPrompts({});
    setIsExample(false);
    setCurrentStep('SELECT_PRODUCTS');
  };

  const handleDownloadResult = async (url: string, index: number) => {
    try {
      const r = await fetch(url);
      const blob = await r.blob();
      const u = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u;
      a.download = `${config.downloadPrefix}-${index + 1}.jpg`;
      a.click();
      window.URL.revokeObjectURL(u);
    } catch (err) {
      const a = document.createElement('a');
      a.href = url;
      a.download = `${config.downloadPrefix}-${index + 1}.jpg`;
      a.click();
    }
  };

  const handleSelectModel = (model: CatalogueGeneratePresetItem) => {
    setSelectedModel(model);
    setSelectedPosesMap({}); // Start with 0 selection
    setCurrentStep('SELECT_POSES');
  };

  const isPoseDisabled = useCallback((poseName: string): boolean => {
    const category = getPoseCategory(poseName);

    if (isActiveWearLayout) {
      if (category === 'upper') return !closeUpTopUrl;
      if (category === 'lower') return !bottomUrl;
      if (category === 'back') return !backUrl;
      if (category === 'full') return !fullTopUrl;
      return true;
    }

    if (category === 'full') {
      return !fullTopUrl;
    }
    if (category === 'upper') {
      return !closeUpTopUrl;
    }
    if (category === 'lower') {
      return !bottomUrl;
    }
    if (category === 'back') {
      return !backUrl;
    }
    return !closeUpSleevelessUrl;
  }, [isActiveWearLayout, fullTopUrl, closeUpTopUrl, bottomUrl, backUrl, closeUpSleevelessUrl]);

  useEffect(() => {
    setSelectedPosesMap(prev => {
      const next = { ...prev };
      let changed = false;
      Object.keys(next).forEach(category => {
        if (isPoseDisabled(category)) {
          delete next[category];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [isPoseDisabled]);

  const togglePose = (pose: CataloguePresetPose) => {
    const category = getPoseCategory(pose.poseName);
    if (isPoseDisabled(pose.poseName)) return;
    setSelectedPosesMap(prev => {
      const next = { ...prev };
      if (next[category]?.cloudStorageUrl === pose.cloudStorageUrl) {
        // clicked the same active pose -> deselect this category entirely
        delete next[category];
      } else {
        // clicked a different pose or category was not active -> set this pose as active
        next[category] = pose;
      }
      return next;
    });
  };

  const groupedPoses = React.useMemo(() => {
    if (!selectedModel) return {} as Record<string, CataloguePresetPose[]>;
    return selectedModel.poses.reduce((acc, pose) => {
      const category = getPoseCategory(pose.poseName);
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(pose);
      return acc;
    }, {} as Record<string, CataloguePresetPose[]>);
  }, [selectedModel]);

  return (
    <div className="bg-white min-h-screen">
      <WorkflowStepper
        steps={steps}
        currentStep={currentStep}
        onStepClick={goToStep}
        onPrev={prevStep}
        onNext={currentStep !== 'SUMMARY' ? nextStep : undefined}
      />

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className={currentStep !== 'SUMMARY' ? 'lg:col-span-4' : 'lg:col-span-3'}>
            {currentStep === 'SELECT_PRODUCTS' && (
              <div className="w-full max-w-6xl mx-auto py-2">


                <div className="bg-gray-50/50 rounded-2xl border border-gray-150 p-6 shadow-sm">
                  <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-5 flex items-center">
                    <span className="w-2.5 h-2.5 bg-secondary-500 rounded-full mr-2"></span>
                    Garment Views
                  </h4>
                  <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${isActiveWearLayout ? 'lg:grid-cols-4' : 'lg:grid-cols-5'}`}>
                    {isActiveWearLayout ? (
                      <>
                        <div className="bg-white p-4 rounded-xl border border-gray-200 hover:border-secondary-400 hover:shadow-md transition-all duration-300">
                          <div className="flex items-center space-x-2.5 mb-3">
                            <span className="w-6 h-6 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center font-bold text-xs">
                              1
                            </span>
                            <span className="font-semibold text-gray-900 text-sm">Top <span className="text-xs text-red-500 font-normal">(Required)</span></span>
                          </div>
                          <ImageUpload
                            imageUrls={closeUpTopUrl ? [closeUpTopUrl] : []}
                            setImageUrls={(urls: any) => {
                              const u = typeof urls === 'function' ? urls([]) : urls;
                              setCloseUpTopUrl(u.length ? u[0] : '');
                              setIsExample(false);
                            }}
                            title=""
                            uploadUrl={garmentUploadUrl}
                            isExample={isExample}
                          />
                          <p className="text-[11px] text-gray-400 mt-2">
                            Upload the top garment image.
                          </p>
                        </div>

                        <div className="bg-white p-4 rounded-xl border border-gray-200 hover:border-secondary-400 hover:shadow-md transition-all duration-300">
                          <div className="flex items-center space-x-2.5 mb-3">
                            <span className="w-6 h-6 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center font-bold text-xs">
                              2
                            </span>
                            <span className="font-semibold text-gray-900 text-sm">Bottom <span className="text-xs text-gray-400 font-normal">(Optional)</span></span>
                          </div>
                          <ImageUpload
                            imageUrls={bottomUrl ? [bottomUrl] : []}
                            setImageUrls={(urls: any) => {
                              const u = typeof urls === 'function' ? urls([]) : urls;
                              setBottomUrl(u.length ? u[0] : '');
                              setIsExample(false);
                            }}
                            title=""
                            uploadUrl={garmentUploadUrl}
                            isExample={isExample}
                          />
                          <p className="text-[11px] text-gray-400 mt-2">
                            Upload the bottom garment image.
                          </p>
                        </div>

                        <div className="bg-white p-4 rounded-xl border border-gray-200 hover:border-secondary-400 hover:shadow-md transition-all duration-300">
                          <div className="flex items-center space-x-2.5 mb-3">
                            <span className="w-6 h-6 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center font-bold text-xs">
                              3
                            </span>
                            <span className="font-semibold text-gray-900 text-sm">Back <span className="text-xs text-gray-400 font-normal">(Optional)</span></span>
                          </div>
                          <ImageUpload
                            imageUrls={backUrl ? [backUrl] : []}
                            setImageUrls={(urls: any) => {
                              const u = typeof urls === 'function' ? urls([]) : urls;
                              setBackUrl(u.length ? u[0] : '');
                              setIsExample(false);
                            }}
                            title=""
                            uploadUrl={garmentUploadUrl}
                            isExample={isExample}
                          />
                          <p className="text-[11px] text-gray-400 mt-2">
                            Upload the back view of the garment.
                          </p>
                        </div>

                        <div className="bg-white p-4 rounded-xl border border-gray-200 hover:border-secondary-400 hover:shadow-md transition-all duration-300">
                          <div className="flex items-center space-x-2.5 mb-3">
                            <span className="w-6 h-6 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center font-bold text-xs">
                              4
                            </span>
                            <span className="font-semibold text-gray-900 text-sm">Full Body <span className="text-xs text-gray-400 font-normal">(Optional)</span></span>
                          </div>
                          <ImageUpload
                            imageUrls={fullTopUrl ? [fullTopUrl] : []}
                            setImageUrls={(urls: any) => {
                              const u = typeof urls === 'function' ? urls([]) : urls;
                              setFullTopUrl(u.length ? u[0] : '');
                              setIsExample(false);
                            }}
                            title=""
                            uploadUrl={garmentUploadUrl}
                            isExample={isExample}
                          />
                          <p className="text-[11px] text-gray-400 mt-2">
                            Upload the full body outfit image.
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                    <div className="bg-white p-4 rounded-xl border border-gray-200 hover:border-secondary-400 hover:shadow-md transition-all duration-300">
                      <div className="flex items-center space-x-2.5 mb-3">
                        <span className="w-6 h-6 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center font-bold text-xs">
                          1
                        </span>
                        <span className="font-semibold text-gray-900 text-sm">Full Top View <span className="text-xs text-red-500 font-normal">(Required)</span></span>
                      </div>
                      <ImageUpload
                        imageUrls={fullTopUrl ? [fullTopUrl] : []}
                        setImageUrls={(urls: any) => {
                          const u = typeof urls === 'function' ? urls([]) : urls;
                          setFullTopUrl(u.length ? u[0] : '');
                          setIsExample(false);
                        }}
                        title=""
                        uploadUrl={garmentUploadUrl}
                        isExample={isExample}
                      />
                      <p className="text-[11px] text-gray-400 mt-2">
                        Upload the full-length front view of the garment.
                      </p>
                    </div>

                    <div className="bg-white p-4 rounded-xl border border-gray-200 hover:border-secondary-400 hover:shadow-md transition-all duration-300">
                      <div className="flex items-center space-x-2.5 mb-3">
                        <span className="w-6 h-6 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center font-bold text-xs">
                          2
                        </span>
                        <span className="font-semibold text-gray-900 text-sm">Close-up Top View <span className="text-xs text-gray-400 font-normal">(Optional)</span></span>
                      </div>
                      <ImageUpload
                        imageUrls={closeUpTopUrl ? [closeUpTopUrl] : []}
                        setImageUrls={(urls: any) => {
                          const u = typeof urls === 'function' ? urls([]) : urls;
                          setCloseUpTopUrl(u.length ? u[0] : '');
                          setIsExample(false);
                        }}
                        title=""
                        uploadUrl={garmentUploadUrl}
                        isExample={isExample}
                      />
                      <p className="text-[11px] text-gray-400 mt-2">
                        Upload a close-up image showing collar, chest, or material details.
                      </p>
                    </div>

                    <div className="bg-white p-4 rounded-xl border border-gray-200 hover:border-secondary-400 hover:shadow-md transition-all duration-300">
                      <div className="flex items-center space-x-2.5 mb-3">
                        <span className="w-6 h-6 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center font-bold text-xs">
                          3
                        </span>
                        <span className="font-semibold text-gray-900 text-sm">Close-up Sleeve View <span className="text-xs text-gray-400 font-normal">(Optional)</span></span>
                      </div>
                      <ImageUpload
                        imageUrls={closeUpSleevelessUrl ? [closeUpSleevelessUrl] : []}
                        setImageUrls={(urls: any) => {
                          const u = typeof urls === 'function' ? urls([]) : urls;
                          setCloseUpSleevelessUrl(u.length ? u[0] : '');
                          setIsExample(false);
                        }}
                        title=""
                        uploadUrl={garmentUploadUrl}
                        isExample={isExample}
                      />
                      <p className="text-[11px] text-gray-400 mt-2">
                        Upload a close-up highlighting sleeves or edge seam work.
                      </p>
                    </div>

                    <div className="bg-white p-4 rounded-xl border border-gray-200 hover:border-secondary-400 hover:shadow-md transition-all duration-300">
                      <div className="flex items-center space-x-2.5 mb-3">
                        <span className="w-6 h-6 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center font-bold text-xs">
                          4
                        </span>
                        <span className="font-semibold text-gray-900 text-sm">Full Bottom View <span className="text-xs text-gray-400 font-normal">(Optional)</span></span>
                      </div>
                      <ImageUpload
                        imageUrls={bottomUrl ? [bottomUrl] : []}
                        setImageUrls={(urls: any) => {
                          const u = typeof urls === 'function' ? urls([]) : urls;
                          setBottomUrl(u.length ? u[0] : '');
                          setIsExample(false);
                        }}
                        title=""
                        uploadUrl={garmentUploadUrl}
                        isExample={isExample}
                      />
                      <p className="text-[11px] text-gray-400 mt-2">
                        Upload the full-length bottom garment image.
                      </p>
                    </div>

                    <div className="bg-white p-4 rounded-xl border border-gray-200 hover:border-secondary-400 hover:shadow-md transition-all duration-300">
                      <div className="flex items-center space-x-2.5 mb-3">
                        <span className="w-6 h-6 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center font-bold text-xs">
                          5
                        </span>
                        <span className="font-semibold text-gray-900 text-sm">Back View <span className="text-xs text-gray-400 font-normal">(Optional)</span></span>
                      </div>
                      <ImageUpload
                        imageUrls={backUrl ? [backUrl] : []}
                        setImageUrls={(urls: any) => {
                          const u = typeof urls === 'function' ? urls([]) : urls;
                          setBackUrl(u.length ? u[0] : '');
                          setIsExample(false);
                        }}
                        title=""
                        uploadUrl={garmentUploadUrl}
                        isExample={isExample}
                      />
                      <p className="text-[11px] text-gray-400 mt-2">
                        Upload the back view of the garment.
                      </p>
                    </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="mt-8 flex justify-end">
                  <button
                    onClick={nextStep}
                    className="bg-secondary-600 hover:bg-secondary-700 text-white font-bold px-8 py-3.5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center space-x-2 group"
                  >
                    <span>Proceed to Model Selection</span>
                    <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
                  </button>
                </div>

                {/* Example Sandbox Template Card */}
                {config.examplePresets && config.examplePresets.length > 0 && (
                <div className="mt-12 bg-white rounded-2xl border border-gray-150 p-6 shadow-sm">
                  <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4 flex items-center">
                    <i className="fas fa-lightbulb text-secondary-500 mr-2"></i>
                    Or start with an example
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {isActiveWearLayout
                      ? (config.examplePresets as ActiveWearExamplePreset[]).map((preset) => {
                        const isSelected = isExample && closeUpTopUrl === preset.top;
                        return (
                          <div
                            key={preset.id}
                            className={`group relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${isSelected
                              ? 'border-secondary-500 ring-2 ring-secondary-100'
                              : 'border-transparent hover:border-gray-200 shadow-sm'
                              }`}
                            onClick={() => {
                              setCloseUpTopUrl(preset.top);
                              setBottomUrl(preset.bottom);
                              setBackUrl(preset.back);
                              setFullTopUrl(preset.full);
                              setCloseUpSleevelessUrl('');
                              setIsExample(true);
                            }}
                          >
                            <div className="grid grid-cols-4 gap-[1.5px] bg-gray-100 aspect-[4/2]">
                              <div className="h-full w-full overflow-hidden">
                                <img src={preset.top} alt="Top" className="w-full h-full object-cover group-hover:scale-110 transition-all duration-550" />
                              </div>
                              <div className="h-full w-full overflow-hidden">
                                <img src={preset.bottom} alt="Bottom" className="w-full h-full object-cover group-hover:scale-110 transition-all duration-550" />
                              </div>
                              <div className="h-full w-full overflow-hidden">
                                <img src={preset.back} alt="Back" className="w-full h-full object-cover group-hover:scale-110 transition-all duration-550" />
                              </div>
                              <div className="h-full w-full overflow-hidden">
                                <img src={preset.full} alt="Full Body" className="w-full h-full object-cover group-hover:scale-110 transition-all duration-550" />
                              </div>
                            </div>
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-2.5">
                              <p className="text-white text-[10px] font-extrabold uppercase tracking-wider">{preset.name}</p>
                            </div>
                          </div>
                        );
                      })
                      : (config.examplePresets as EcommerceExamplePreset[]).map((preset) => {
                      const isSelected = isExample && fullTopUrl === preset.fullTop;
                      return (
                        <div
                          key={preset.id}
                          className={`group relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${isSelected
                            ? 'border-secondary-500 ring-2 ring-secondary-100'
                            : 'border-transparent hover:border-gray-200 shadow-sm'
                            }`}
                          onClick={() => {
                            setFullTopUrl(preset.fullTop);
                            setCloseUpTopUrl(preset.closeUpTop);
                            setCloseUpSleevelessUrl(preset.closeUpSleeveless);
                            setBottomUrl('');
                            setIsExample(true);
                          }}
                        >
                          <div className="grid grid-cols-3 gap-[1.5px] bg-gray-100 aspect-[3/2]">
                            <div className="h-full w-full overflow-hidden">
                              <img src={preset.fullTop} alt="Full Top" className="w-full h-full object-cover group-hover:scale-110 transition-all duration-550" />
                            </div>
                            <div className="h-full w-full overflow-hidden">
                              <img src={preset.closeUpTop} alt="Close-up Top" className="w-full h-full object-cover group-hover:scale-110 transition-all duration-550" />
                            </div>
                            <div className="h-full w-full overflow-hidden">
                              <img src={preset.closeUpSleeveless} alt="Sleeve Detail" className="w-full h-full object-cover group-hover:scale-110 transition-all duration-550" />
                            </div>
                          </div>
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-2.5">
                            <p className="text-white text-[10px] font-extrabold uppercase tracking-wider">{preset.name}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                )}
              </div>
            )}

            {currentStep === 'SELECT_MODELS' && (
              <div className="w-full max-w-6xl mx-auto py-2">


                {presetsLoading ? (
                  <div className="flex flex-col items-center justify-center py-20 space-y-4">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-secondary-600"></div>
                    <p className="text-sm text-gray-550">Loading model presets...</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
                    {presets.map((model) => {
                      const isSelected = selectedModel?.modelName === model.modelName;
                      const displayImg = model.profilePic
                        ? (model.profilePic.startsWith('http') ? model.profilePic : `https://${model.profilePic}`)
                        : (model.faceImageUrl || model.poses[0]?.cloudStorageUrl || '');
                      return (
                        <div
                          key={model.modelName}
                          onClick={() => handleSelectModel(model)}
                          className={`relative rounded-2xl overflow-hidden border-2 cursor-pointer bg-white transition-all duration-300 shadow-sm ${isSelected
                            ? 'border-secondary-500 ring-4 ring-secondary-100 scale-[1.02]'
                            : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                            }`}
                        >
                          <div className="aspect-[3/4] relative">
                            {displayImg && <img src={displayImg} alt={model.modelName} className="w-full h-full object-cover" />}
                            <div className="absolute top-3 left-3 bg-secondary-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wider shadow-sm">
                              Studio Preset
                            </div>
                            {isSelected && (
                              <div className="absolute top-3 right-3 bg-secondary-500 text-white rounded-full p-1 shadow-md">
                                <i className="fas fa-check text-[10px]"></i>
                              </div>
                            )}
                          </div>
                          <div className="p-4 border-t border-gray-100">
                            <p className="text-sm font-bold text-gray-900 capitalize truncate">{model.modelName}</p>
                            <p className="text-[11px] text-gray-450 mt-0.5">{model.poses.length} perspectives available</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {currentStep === 'SELECT_POSES' && selectedModel && (
              <div className="w-full max-w-6xl mx-auto">
                <div className="flex justify-between items-center mb-4 bg-gray-50/50 p-4 rounded-xl border border-gray-150">
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center">
                    <span className="w-2.5 h-2.5 bg-secondary-500 rounded-full mr-2"></span>
                    Configuring {selectedPoseNames.length} of {selectedModel.poses.length} poses
                  </h4>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        const nextMap: Record<string, CataloguePresetPose> = {};
                        selectedModel.poses.forEach(pose => {
                          const category = getPoseCategory(pose.poseName);
                          if (!isPoseDisabled(pose.poseName)) {
                            // If a category has multiple poses, we pick the first one by default for select all
                            if (!nextMap[category]) {
                              nextMap[category] = pose;
                            }
                          }
                        });
                        setSelectedPosesMap(nextMap);
                      }}
                      className="text-xs font-semibold text-secondary-600 hover:text-secondary-700 bg-secondary-50 hover:bg-secondary-100 px-3 py-1.5 rounded-lg transition-colors border border-secondary-100 shadow-sm"
                    >
                      Select All
                    </button>
                    <button
                      onClick={() => setSelectedPosesMap({})}
                      className="text-xs font-semibold text-gray-500 hover:text-gray-655 bg-gray-50 hover:bg-gray-100 px-3 py-1.5 rounded-lg transition-colors border border-gray-200 shadow-sm"
                    >
                      Deselect All
                    </button>
                  </div>
                </div>

                <div className="space-y-6">
                  {Object.entries(groupedPoses).map(([category, posesInGroup]) => {
                    const formattedGroupName = getUploadRequirementLabel(category);

                    return (
                      <div key={category} className="space-y-4">
                        <div className="flex items-center space-x-4">
                          <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center">
                            <span className="w-2.5 h-2.5 bg-secondary-500 rounded-full mr-2"></span>
                            {formattedGroupName} Poses ({posesInGroup.length})
                          </h3>
                          <div className="h-[1px] flex-grow bg-gradient-to-r from-gray-200 to-transparent"></div>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                          {posesInGroup.map((pose, idx) => {
                            const isSelected = selectedPosesMap[category]?.cloudStorageUrl === pose.cloudStorageUrl;
                            const garmentUrl = getGarmentUrlForCategory(category);
                            const isDisabled = isPoseDisabled(pose.poseName);

                            return (
                              <div
                                key={`${pose.poseName}-${idx}`}
                                onClick={() => togglePose(pose)}
                                className={`relative rounded-2xl overflow-hidden border-2 bg-white transition-all duration-300 shadow-sm ${isDisabled
                                  ? 'border-gray-150 opacity-50 cursor-not-allowed'
                                  : isSelected
                                    ? 'border-secondary-500 ring-4 ring-secondary-100 scale-[1.02] cursor-pointer'
                                    : 'border-gray-200 hover:border-gray-300 hover:shadow-md cursor-pointer'
                                  }`}
                              >
                                <div className="aspect-[3/4] relative">
                                  <img src={pose.cloudStorageUrl} alt={pose.poseName} className="w-full h-full object-cover" />

                                  {/* Selection / Lock indicator */}
                                  {!isDisabled ? (
                                    <div className={`absolute top-3 right-3 rounded-full p-1.5 shadow-md transition-all ${isSelected ? 'bg-secondary-500 text-white' : 'bg-white/80 text-gray-400 border border-gray-200'}`}>
                                      <i className={`fas ${isSelected ? 'fa-check' : 'fa-plus'} text-[10px] w-3 h-3 flex items-center justify-center`}></i>
                                    </div>
                                  ) : (
                                    <div className="absolute top-3 right-3 rounded-full p-1.5 shadow-md bg-gray-150/90 text-gray-500 border border-gray-200">
                                      <i className="fas fa-lock text-[10px] w-3 h-3 flex items-center justify-center"></i>
                                    </div>
                                  )}

                                  <div className="absolute top-3 left-3 bg-black/60 text-white text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wider shadow-sm">
                                    {pose.poseName.replace('-', ' ')}
                                  </div>

                                  {/* Requirement overlay for disabled poses */}
                                  {isDisabled && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/5 backdrop-blur-[0.5px]">
                                      <span className="bg-black/75 text-white text-[9px] font-bold px-2.5 py-1.5 rounded-lg shadow-md text-center max-w-[85%] leading-relaxed">
                                        Go Back and Upload {getUploadRequirementLabel(category)}
                                      </span>
                                    </div>
                                  )}

                                  {/* Garment preview overlay */}
                                  {!isDisabled && garmentUrl && (
                                    <div className="absolute bottom-3 left-3 rounded-lg overflow-hidden shadow-md">
                                      <img src={garmentUrl} alt="Garment" className="w-9 h-12 object-cover border-2 border-white rounded-lg" />
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-8 flex justify-between">
                  <button
                    onClick={() => setCurrentStep('SELECT_MODELS')}
                    className="border border-gray-300 hover:bg-gray-50 text-gray-700 font-bold px-6 py-3 rounded-xl transition-all duration-200 shadow-sm"
                  >
                    Back to Models
                  </button>
                  <button
                    onClick={() => {
                      if (selectedPoseNames.length === 0) {
                        alert('Please select at least one pose/angle.');
                        return;
                      }
                      setCurrentStep('SUMMARY');
                    }}
                    className="bg-secondary-600 hover:bg-secondary-700 text-white font-bold px-8 py-3.5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center space-x-2 group"
                  >
                    <span>Proceed to Summary</span>
                    <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
                  </button>
                </div>
              </div>
            )}

            {currentStep === 'SUMMARY' && (
              <div className="w-full max-w-6xl mx-auto py-2">
                {generatedImages.length > 0 ? (
                  <GeneratedResults
                    images={generatedImages}
                    onStartNew={handleStartNew}
                    onDownload={handleDownloadResult}
                    onNavigateToCanvas={(url: string, tool: string) =>
                      navigate('/canvas-editor', { state: { inputImages: [url], tool } })
                    }
                  />
                ) : (
                  <div className="space-y-6">
                    {isProcessing ? (
                      <div
                        className="flex flex-col items-center justify-center bg-gray-50/50 rounded-2xl border border-gray-200/80 p-8 text-center"
                        style={{ minHeight: 'calc(100vh - 220px)' }}
                      >
                        <div className="space-y-6 max-w-sm mx-auto">
                          <div className="relative w-20 h-20 mx-auto flex items-center justify-center">
                            <div className="absolute inset-0 rounded-full border-4 border-secondary-100 animate-pulse"></div>
                            <div className="absolute inset-0 rounded-full border-4 border-t-secondary-600 animate-spin"></div>
                            <span className="text-xs font-mono font-bold text-secondary-700 z-10">{generationTime.toFixed(1)}s</span>
                          </div>
                          <div>
                            <h4 className="text-lg font-bold text-gray-800 mb-1">Generating Lookbook</h4>
                            <p className="text-xs text-secondary-600 font-medium animate-pulse">{processingStage}</p>
                          </div>
                          <p className="text-xs text-gray-400">
                            Synthesizing the selected perspective views. This may take up to a minute.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-8">

                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                          {Object.entries(selectedPosesMap).map(([category, pose]) => {
                            const garmentUrl = getGarmentUrlForCategory(category);

                            return (
                              <div key={category} className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm space-y-3.5 hover:shadow-md transition-shadow">
                                <div className="flex items-center justify-between border-b border-gray-100 pb-2.5">
                                  <span className="text-xs font-bold text-gray-900 capitalize flex items-center">
                                    <span className="w-2.5 h-2.5 bg-secondary-500 rounded-full mr-2"></span>
                                    {getUploadRequirementLabel(category)} View
                                  </span>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  {/* Garment Image */}
                                  <div className="relative rounded-xl overflow-hidden bg-gray-50 border border-gray-100 aspect-[3/4]">
                                    <img src={garmentUrl} alt="Garment" className="w-full h-full object-cover" />
                                    <div className="absolute bottom-2 left-2 bg-black/60 text-white text-[8px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider">
                                      Garment
                                    </div>
                                  </div>

                                  {/* Model Pose Reference */}
                                  <div className="relative rounded-xl overflow-hidden bg-gray-50 border border-gray-100 aspect-[3/4]">
                                    <img src={pose.cloudStorageUrl} alt="Model Pose" className="w-full h-full object-cover" />
                                    <div className="absolute bottom-2 left-2 bg-secondary-600/90 text-white text-[8px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider">
                                      Model
                                    </div>
                                  </div>
                                </div>

                                <div className="pt-2 border-t border-gray-100 space-y-1">
                                  <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                                    Suffix Prompt (Optional)
                                  </label>
                                  <textarea
                                    value={suffixPrompts[pose.poseName] || ''}
                                    onChange={(e) => setSuffixPrompts(prev => ({ ...prev, [pose.poseName]: e.target.value }))}
                                    placeholder={`Extra details like - "with white leggings"`}
                                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent transition-all min-h-[60px] resize-none bg-gray-50/30 hover:bg-white focus:bg-white"
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Action Row */}
                        <div className="pt-6 border-t border-gray-150 flex justify-between items-center">
                          <button
                            onClick={() => setCurrentStep('SELECT_POSES')}
                            className="border border-gray-300 hover:bg-gray-50 text-gray-700 font-bold px-6 py-3 rounded-xl transition-all duration-200 shadow-sm"
                          >
                            Modify Angles
                          </button>
                          <p className="text-xs text-gray-450 font-medium">
                            Ready to generate {selectedPoseNames.length} catalog image{selectedPoseNames.length > 1 ? 's' : ''}.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {currentStep === 'SUMMARY' && (
            <div className="lg:col-span-1 animate-slideDown">
              <SummarySidebar
                inputImageUrls={inputImageUrls}
                selectedModelUrl={selectedModel?.faceImageUrl || selectedModel?.profilePic || selectedModel?.poses[0]?.cloudStorageUrl}
                selectedModelName={selectedModel?.modelName || 'Not selected'}
                selectedBackgroundUrl={null}
                selectedBackgroundName="Model Original"
                selectedPoses={selectedPoseNames.map(name => ({ id: name, type: name }))}
                mode={mode}
                onModeChange={setMode}
                availableModes={modeRestrictions.availableModes}
                prompt={""}
                onPromptChange={() => { }}
                showAdvancedSettings={false}
                onToggleAdvanced={() => { }}
                creditEstimation={creditEstimation}
                onGenerate={handleGenerate}
                isProcessing={isProcessing}
                hidePrompt={true}
                generationTime={generationTime}
                enable4k={enable4k}
                onEnable4kChange={setEnable4k}
                showEnable4kCheckbox={true}
              />
            </div>
          )}
        </div>
      </div>

      <InsufficientCreditsModal
        isOpen={showInsufficientCreditsModal}
        onClose={() => setShowInsufficientCreditsModal(false)}
        requiredCredits={creditEstimation.creditCost || 0}
        availableCredits={userCredits}
        featureName={config.featureName}
      />
    </div>
  );
};

export default UpperBodyFashionCatalogueWorkflow;
